import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ScrollReveal, SplitReveal } from '@/components/motion';
import { ADMISSION_TRACKS, type AdmissionTrackKey } from '@/data/admissions';
import { Button, cn } from '@ibas/ui';

/**
 * AdmissionProcess (Admissions · section 3) — "Four steps to your place at IBAS."
 *
 * An interactive Online / On-site switch built from ADMISSION_TRACKS. The active
 * key lives in useState (default 'online'); a gold underline + navy text marks the
 * live tab while inactive tabs sit muted, with a role="tablist"/"tab" +
 * aria-selected contract and visible focus rings. Selecting a route re-keys the
 * ScrollReveal so its numbered four-step timeline restages — the gold "01…04"
 * numerals staggering back in — while a vertical connector threads the steps.
 * A primary (navy) CTA on this light background closes the section. No fees,
 * dates or facts beyond those carried in the imported step copy.
 */
export function AdmissionProcess() {
  const [activeKey, setActiveKey] = useState<AdmissionTrackKey>('online');
  const activeTrack =
    ADMISSION_TRACKS.find((track) => track.key === activeKey) ?? ADMISSION_TRACKS[0];

  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Admission process
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Four steps to your place at IBAS.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-4 text-base text-muted-foreground md:text-lg">
              Two routes to the same standard — study fully online on rolling monthly intakes, or
              on-site in Thalwil on scheduled intakes. Choose a route to see the steps.
            </p>
          </ScrollReveal>
        </header>

        {/* Route switch — a two-tab control, gold accent on the active route. */}
        <ScrollReveal from="up" delay={0.15}>
          <div
            role="tablist"
            aria-label="Admission routes"
            className="mt-10 flex flex-wrap gap-2 border-b border-border"
          >
            {ADMISSION_TRACKS.map((track) => {
              const isActive = track.key === activeKey;
              return (
                <button
                  key={track.key}
                  type="button"
                  role="tab"
                  id={`admission-tab-${track.key}`}
                  aria-selected={isActive}
                  aria-controls={`admission-panel-${track.key}`}
                  onClick={() => setActiveKey(track.key)}
                  className={cn(
                    '-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-3 font-label text-sm tracking-[0.12em] uppercase transition-colors duration-300 outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none',
                    isActive
                      ? 'border-brand-gold text-brand-navy'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}
                >
                  <track.Icon
                    aria-hidden
                    className={cn(
                      'size-4 shrink-0 transition-colors duration-300 motion-reduce:transition-none',
                      isActive ? 'text-brand-gold' : 'text-muted-foreground',
                    )}
                  />
                  {track.label}
                </button>
              );
            })}
          </div>
        </ScrollReveal>

        {/* Active route tagline. */}
        <div
          id={`admission-panel-${activeTrack.key}`}
          role="tabpanel"
          aria-labelledby={`admission-tab-${activeTrack.key}`}
          tabIndex={0}
          className="outline-none"
        >
          <p className="mt-8 max-w-2xl font-display text-xl leading-snug text-foreground/90 md:text-2xl">
            {activeTrack.tagline}
          </p>

          {/* Numbered timeline — re-keyed on the active route so it restages. */}
          <ScrollReveal
            key={activeTrack.key}
            stagger={0.1}
            className="relative mt-10 grid grid-cols-1 gap-8 md:grid-cols-2"
          >
            {activeTrack.steps.map((step, index) => (
              <div key={step.title} className="relative flex gap-5">
                {/* Numeral + connector rail. */}
                <div className="flex flex-col items-center">
                  <span className="font-display text-4xl leading-none font-semibold text-brand-gold md:text-5xl">
                    {`0${index + 1}`}
                  </span>
                  {index < activeTrack.steps.length - 1 && (
                    <span
                      aria-hidden
                      className="mt-3 hidden w-px flex-1 bg-gradient-to-b from-brand-gold/40 to-transparent md:block"
                    />
                  )}
                </div>
                <div className="pb-2">
                  <h3 className="font-display text-xl leading-tight font-semibold text-foreground">
                    {step.title}
                  </h3>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </ScrollReveal>
        </div>

        <ScrollReveal from="up" delay={0.1}>
          <div className="mt-12">
            <Button asChild size="lg" variant="primary" data-cursor>
              <Link to="/apply">Start your application</Link>
            </Button>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
